import { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import useUpdatedRef from '../../../utils/react/useUpdatedRef';
import {
    deselectElementChip,
    finishEditingElementChip,
    selectElementChip,
    startEditingElementChip,
} from '../elementChipsActions';
import { selectEditingElementChip, selectSelectedElementChip } from '../elementChipsSelectors';
import type { ElementChipDescriptor } from '../elementChipsTypes';
import { useChipSelectionScope } from './ChipSelectionScope';

type ChipSelectionState = {
    isSelected: boolean;
    /** Whether the chip can currently be selected (write access plus the single-selection gate). */
    canSelect: boolean;
    isEditing: boolean;
    select: () => void;
    deselect: () => void;
    setEditing: (isEditing: boolean) => void;
};

/**
 * Owns the global selection and inline-editing state for one select-mode chip.
 */
export const useChipSelectionState = ({
    chip,
    canModify,
}: {
    chip: ElementChipDescriptor;
    canModify: boolean;
}): ChipSelectionState => {
    const dispatch = useDispatch();
    const { containerId, elementId, isElementSingleSelected } = useChipSelectionScope();

    const selectedChip = useSelector(selectSelectedElementChip);
    const editingChip = useSelector(selectEditingElementChip);
    const chipSelection = { containerId, elementId, chip };
    const isSelected =
        selectedChip?.containerId === containerId &&
        selectedChip.elementId === elementId &&
        selectedChip.chip.id === chip.id;
    const canSelect = canModify && isElementSingleSelected;
    const isEditing =
        editingChip?.containerId === containerId &&
        editingChip.elementId === elementId &&
        editingChip.chip.id === chip.id;

    const deselect = useCallback(() => {
        dispatch(deselectElementChip());
    }, [dispatch]);

    const select = useCallback(() => {
        dispatch(selectElementChip(chipSelection));
    }, [chipSelection, dispatch]);

    const setEditing = useCallback(
        (editing: boolean) => {
            if (!editing) {
                dispatch(finishEditingElementChip(chipSelection));
                return;
            }
            if (!canSelect) return;

            dispatch(startEditingElementChip(chipSelection));
        },
        [canSelect, chipSelection, dispatch],
    );

    // Deselect when the owning element stops being the single canvas selection.
    useEffect(() => {
        if (isSelected && !isElementSingleSelected) deselect();
    }, [deselect, isElementSingleSelected, isSelected]);

    // Deselect when this chip unmounts while selected (chip removed, or its container unmounted).
    const isSelectedRef = useUpdatedRef(isSelected);
    const deselectRef = useUpdatedRef(deselect);
    useEffect(
        () => () => {
            if (isSelectedRef.current) deselectRef.current();
        },
        [deselectRef, isSelectedRef],
    );

    return { isSelected, canSelect, isEditing, select, deselect, setEditing };
};
